<script setup lang="ts">
import Backspace from "#ui/assets/icons/Backspace.vue";
import type { DecorationBinding } from "#ui/decoration/types";

export interface Props {
	withClear?: boolean;
	withDecimal?: boolean;
	clearLabel?: string;
	decimalSeparator?: string;
	disabled?: boolean;
	keyAttrs?: DecorationBinding;
}

const props = withDefaults(defineProps<Props>(), {
	withClear: true,
	withDecimal: false,
	clearLabel: "C",
	decimalSeparator: ",",
	disabled: false,
	keyAttrs: undefined,
});

const emit = defineEmits<{
	digit: [value: string];
	separator: [];
	clear: [];
	backspace: [];
}>();

const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const KEY_LAYOUT =
	"tw:flex tw:min-h-[var(--keypad-key-size,56px)] tw:min-w-44 tw:items-center tw:justify-center tw:rounded-[var(--keypad-key-radius,14px)] tw:p-0 tw:[font-family:var(--fontMain)] tw:[font-weight:normal] tw:select-none tw:[-webkit-tap-highlight-color:transparent] tw:[border:1px_solid_var(--keypad-key-border,transparent)] tw:[font-size:var(--keypad-key-font,calc(1.4rem*var(--fontFit-x,1)))] tw:[outline:none] tw:[transition:transform_0.12s_ease,background-color_0.2s_ease,opacity_0.2s_ease] tw:active:[background:var(--keypad-key-bg-active,var(--keypad-key-bg,var(--bgButtonColor)))] tw:active:[transform:scale(0.96)] tw:disabled:opacity-45 tw:min-[1023px]:min-h-[var(--keypad-key-size,72px)] tw:min-[1023px]:[font-size:var(--keypad-key-font,calc(1.8rem*var(--fontFit-x,1)))]";
const KEY_COLORS = "tw:[background:var(--keypad-key-bg,var(--cardsColor))] tw:[color:var(--keypad-key-color,inherit)]";
const ACTION_COLORS =
	"tw:[background:var(--keypad-action-bg,var(--keypad-key-bg,var(--cardsColor)))] tw:[color:var(--keypad-action-color,var(--keypad-key-color,inherit))]";

const keyClass = `numeric_keypad_key ${KEY_LAYOUT} ${KEY_COLORS}`;
const actionKeyClass = `numeric_keypad_key numeric_keypad_key_action ${KEY_LAYOUT} ${ACTION_COLORS}`;
const wideKeyClass = `numeric_keypad_key numeric_keypad_key_action numeric_keypad_key_wide ${KEY_LAYOUT} ${ACTION_COLORS} tw:col-span-full`;
</script>

<template>
	<div class="numeric_keypad tw:grid tw:w-full tw:grid-cols-[repeat(3,1fr)] tw:gap-[var(--keypad-gap,12px)] tw:min-[1023px]:gap-[var(--keypad-gap,16px)]">
		<button
			v-for="digit in digits"
			:key="digit"
			type="button"
			:class="keyClass"
			:disabled="props.disabled"
			v-bind="props.keyAttrs"
			@click="emit('digit', digit)"
		>
			{{ digit }}
		</button>
		<button v-if="props.withClear" type="button" :class="actionKeyClass" :disabled="props.disabled" v-bind="props.keyAttrs" @click="emit('clear')">
			{{ props.clearLabel }}
		</button>
		<span v-else class="numeric_keypad_spacer tw:block" />
		<button type="button" :class="keyClass" :disabled="props.disabled" v-bind="props.keyAttrs" @click="emit('digit', '0')">0</button>
		<button type="button" :class="actionKeyClass" :disabled="props.disabled" aria-label="backspace" v-bind="props.keyAttrs" @click="emit('backspace')">
			<Backspace
				class="numeric_keypad_backspace tw:pointer-events-none tw:h-auto tw:w-[var(--keypad-icon-size,30px)] tw:min-[1023px]:w-[var(--keypad-icon-size,38px)]"
			/>
		</button>
		<button v-if="props.withDecimal" type="button" :class="wideKeyClass" :disabled="props.disabled" v-bind="props.keyAttrs" @click="emit('separator')">
			{{ props.decimalSeparator }}
		</button>
	</div>
</template>
